import { createContext, useContext, useEffect, useState, useRef } from 'react'
import { useAuth, useUser } from '.'
import { reportsRoutes } from '../routes'
import { useConnectionContext } from './Connection.context'
import { useSitesContext } from './Sites.context'
import MachinesService from '../services/machines.service'

export const MachineContext = createContext()

export const MachineProvider = (props) => {
    const { isAuthenticated } = useAuth()
    const {
        site,
        admin,
        isOperator,
        isUniversalInspector,
        isUniversalChief,
        isOperatorInspector
    } = useUser()
    const { siteSelected } = useSitesContext()
    const [machines, setMachines] = useState([])
    const [machinesOfProject, setMachinesOfProject] = useState([])
    const [machinesBySite, setMachinesBySite] = useState([])
    const [machineSelected, setMachineSelected] = useState()
    const [tipoMaquinas, setTipoMaquinas] = useState([])
    const [marcasMaquinas, setMarcasMaquinas] = useState([])
    const [modelosMaquinas, setModelosMaquinas] = useState([])
    const [machinesByModel, setMachinesByModel] = useState([])
    const [machineDetail, setMachineDetail] = useState(null)
    const [machineReports, setMachineReports] = useState([])
    const [machinesListSearchTerm, setMachinesListSearchTerm] = useState(null)
    const [machineIsFetchingRef] = useState({ current: false })
    const { isOnline } = useConnectionContext()
    // const urlImagenes = 'https://icvmantencion.blob.core.windows.net/plataforma-mantencion/maquinas/imagenes/' // Unused in original?

    const fetchingEquidRef = useRef(null)
    const cachedReportsRef = useRef({}) // Cache structure: { [equid]: { machineDetail, machineReports } }

    const fetchMachineDetailsAndReports = async (equid) => {
        if (!isOnline) {
            alert(
                'Dispositivo sin conexión a internet. No se pueden obtener los detalles.'
            )
            return
        }

        // Stale-While-Revalidate: load from cache immediately if available to prevent layout flash
        if (cachedReportsRef.current[equid]) {
            setMachineDetail(cachedReportsRef.current[equid].machineDetail)
            setMachineReports(cachedReportsRef.current[equid].machineReports)
        } else {
            setMachineDetail(null)
            setMachineReports(null)
        }

        fetchingEquidRef.current = equid

        try {
            const [machineDetailsResponse, reportsResponse] = await Promise.all([
                MachinesService.getMachineByEquid(equid),
                reportsRoutes.getReportByEquid(equid)
            ])

            let details = null
            let reports = []

            if (machineDetailsResponse && machineDetailsResponse.data) {
                details = machineDetailsResponse.data
                setMachineDetail(details)
            }

            if (reportsResponse && reportsResponse.data) {
                reports = reportsResponse.data
                setMachineReports(reports)
            } else if (Array.isArray(reportsResponse)) {
                reports = reportsResponse
                setMachineReports(reports)
            }

            // Save to cache
            cachedReportsRef.current[equid] = {
                machineDetail: details,
                machineReports: reports
            }
        } catch (error) {
            console.error('Error fetching machine details and reports:', error)
        } finally {
            fetchingEquidRef.current = null
        }
    }

    useEffect(() => {
        const isUniversal = isUniversalInspector || isUniversalChief
        if (
            isAuthenticated &&
			(!isOperator || isUniversal || isOperatorInspector)
        ) {
            if (machinesOfProject.length === 0) {
                getMachinesData()
            }

            if (isOnline) {
                if (tipoMaquinas.length === 0) leerTipoMaquinas()
                if (marcasMaquinas.length === 0) leerMarcasMaquinas()
                if (modelosMaquinas.length === 0) leerModelosMaquinas()
            }
        }
    }, [
        isAuthenticated,
        isOnline,
        isOperator,
        admin,
        isUniversalInspector,
        isUniversalChief,
        isOperatorInspector
    ])

    useEffect(() => {
        const isUniversal = isUniversalInspector || isUniversalChief
        const canFetch = admin || !isOperator || isUniversal || isOperatorInspector
        if (canFetch && (machines.length > 0 || siteSelected)) {
            if (machineIsFetchingRef.current) return
            machineIsFetchingRef.current = true
            getMachinesBySiteData().finally(() => {
                machineIsFetchingRef.current = false
            })
        }
    }, [
        machines,
        isOnline,
        siteSelected,
        site,
        admin,
        isOperator,
        isUniversalInspector,
        isUniversalChief,
        isOperatorInspector
    ])

    const getMachinesData = async () => {
        const canFetchAll =
			admin ||
			!isOperator ||
			isOperatorInspector ||
			isUniversalInspector ||
			isUniversalChief
        if (!siteSelected && !canFetchAll) {
            return
        }
        try {
            const isUniversal = isUniversalInspector || isUniversalChief
            const data = await MachinesService.getMachines(
                isOnline,
                isOperator,
                admin,
                isUniversal,
                isOperatorInspector
            )
            setMachines(data)
            if (isOnline) {
                setMachinesOfProject(data)
            }
        } catch (error) {
            console.error('Failed to get machines', error)
        }
    }

    const getMachinesBySiteData = async () => {
        try {
            const isAllSites =
				siteSelected &&
				(siteSelected.idObra === 'none' || siteSelected._id === 'all' || siteSelected._id === 'universal')
            const targetSite = isAllSites ? 'all' : siteSelected || site

            const data = await MachinesService.getMachinesBySite(
                isOnline,
                targetSite,
                admin
            )
            const filtered = data.filter(m =>
                (m.idpminspeccion !== null && m.idpminspeccion !== undefined && m.idpminspeccion !== '') ||
                (m.idpmmantencion !== null && m.idpmmantencion !== undefined && m.idpmmantencion !== '')
            )
            setMachinesBySite(filtered)
        } catch (error) {
            console.error('Failed to get machines by site', error)
        }
    }

    // Legacy functions kept but could be moved to service eventually
    const sumarTipoMaquina = async (tipo) => {
        const response = await MachinesService.addType(tipo)
        leerTipoMaquinas()
        return response
    }

    const sumarMarcaMaquina = async (marca) => {
        const response = await MachinesService.addBrand(marca)
        leerMarcasMaquinas()
        return response
    }

    const sumarModeloMaquina = async (marcaId, modelo) => {
        const response = await MachinesService.addModel(marcaId, modelo)
        leerModelosMaquinas()
        return response
    }

    const leerTipoMaquinas = async () => {
        const isUniversal = isUniversalInspector || isUniversalChief
        const response = await MachinesService.getTypes(
            isOperator,
            isUniversal,
            isOperatorInspector
        )
        setTipoMaquinas(response)
    }

    const leerMarcasMaquinas = async () => {
        const isUniversal = isUniversalInspector || isUniversalChief
        const response = await MachinesService.getBrands(
            isOperator,
            isUniversal,
            isOperatorInspector
        )
        setMarcasMaquinas(response)
    }

    const leerModelosMaquinas = async () => {
        const isUniversal = isUniversalInspector || isUniversalChief
        const response = await MachinesService.getModels(
            isOperator,
            isUniversal,
            isOperatorInspector
        )
        setModelosMaquinas(response)
    }

    const fetchMachinesByModel = async (model) => {
        const response = await MachinesService.getMachinesByModel(model)
        const filtered = response.filter(m =>
            (m.idpminspeccion !== null && m.idpminspeccion !== undefined && m.idpminspeccion !== '') ||
            (m.idpmmantencion !== null && m.idpmmantencion !== undefined && m.idpmmantencion !== '')
        )
        setMachinesByModel(filtered)
    }

    // Re-exposed for compatibility if components call it directly, but now uses internal wrapper
    const getMachines = getMachinesData

    const nuevaMaquina = async (maquina) => {
        if (maquina.type) {
            if (maquina.brand) {
                if (maquina.model) {
                    const isUniversal = isUniversalInspector || isUniversalChief
                    const response = await MachinesService.createMachineOfProject(
                        maquina,
                        isOperator,
                        isUniversal,
                        isOperatorInspector
                    )
                    getMachines()
                    return response
                } else {
                    alert('Falta Modelo de Equipo')
                }
            } else {
                alert('Falta Marca de Equipo')
            }
        } else {
            alert('Falta Tipo de Equipo')
        }
    }

    const editarMaquina = async (maquina) => {
        if (maquina.type) {
            if (maquina.brand) {
                if (maquina.model) {
                    const response = await MachinesService.editMachine(maquina)
                    getMachines()
                    return response
                } else {
                    alert('Falta Modelo de Equipo')
                }
            } else {
                alert('Falta Marca de Equipo')
            }
        } else {
            alert('Falta Tipo de Equipo')
        }
    }

    const asignarTipoMaquina = async (machineOfProjectId, typeId) => {
        const isUniversal = isUniversalInspector || isUniversalChief
        const response = await MachinesService.assignTypeToMachineOfProject(
            machineOfProjectId,
            typeId,
            isOperator,
            isUniversal,
            isOperatorInspector
        )
        if (response) {
            getMachines()
        }
        return response
    }

    const borrarMaquina = async (id) => {
        const response = await MachinesService.deleteMachine(id)
        if (response) {
            getMachines()
        }
        return response
    }

    const provider = {
        setMachines,
        machines,
        machinesOfProject,
        machinesBySite,
        machineSelected,
        setMachineSelected,
        nuevaMaquina,
        editarMaquina,
        borrarMaquina,
        tipoMaquinas,
        sumarTipoMaquina,
        marcasMaquinas,
        sumarMarcaMaquina,
        modelosMaquinas,
        sumarModeloMaquina,
        getMachines,
        syncMachinesBySiteId: MachinesService.syncMachinesBySiteId,
        asignarTipoMaquina,
        machinesByModel,
        fetchMachinesByModel,
        machineDetail,
        machineReports,
        fetchMachineDetailsAndReports,
        machinesListSearchTerm,
        setMachinesListSearchTerm
    }

    return <MachineContext.Provider value={provider} {...props} />
}

export const useMachineContext = () => useContext(MachineContext)
